/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */
import {useTranslation} from "react-i18next";
import React from "react";
import {
    CubeIcon,
    IconArrowEntry,
    IconArrowExit,
    IconCube3d,
    IconDeliveryTruck,
    IconDocumentVehicleGate,
    IconDocumentWarning,
    IconGuardTitle,
    IconTrailer
} from "../../../../../../components/buttons/IconButtons";
import {STATUS_MODEL} from "../../../../../../enums/statusEnum";
import {
    ENTRY_AND_EXIT_FORMS,
    MATERIAL_CONTROL_AUDIT_ENTITY,
    MOVEMENT_DIRECTION,
    getFieldAudit,
    getGateLogEmptyText,
    getGateRegisteredSource,
    getMovementDirectionOptionByValue,
    getPackagingQuantityRecord,
    getRegisteredByIcon,
    hasGateLogValue,
    wasMaterialControlDispatched
} from "../../../Model/Model";
import GateLogCard from "./Sections/GateLogCard";
import GateLogGroup from "./Sections/GateLogGroup";
import GateLogValueBox from "./Sections/GateLogValueBox";
import TransportSection from "./Sections/TransportSection";
import RegisteredByChip from "./Sections/RegisteredByChip";
import DiscrepancyBanner from "./Sections/DiscrepancyBanner";
import MaterialsSection, {MATERIALS_MODE} from "./Sections/MaterialsSection";

/**
 * Moment of the movement the list is read in.
 *
 * A movement stays OPEN until the guard at the gate closes it, is left in
 * DISCREPANCY when what leaves the plant does not match what was asked for, and
 * is DISPATCHED once it was registered without anything to review.
 */
const PHASE = {
    OPEN: 'OPEN',
    DISCREPANCY: 'DISCREPANCY',
    DISPATCHED: 'DISPATCHED'
};

/**
 * User a value of the gate log is named after: the carrier that arrives at the
 * plant with the materials, or the guard that closes the movement at the gate.
 *
 * The icon of the chip is not declared here, because it is the user that wrote
 * the value the one that says whether it was registered at the gate or with the
 * folio.
 */
const REGISTERED_BY = {
    CARRIER: {key: 'CARRIER'},
    GUARD: {key: 'GUARD'},
    MOVEMENT_DATE: {key: 'MOVEMENT_DATE'},
    REQUESTER: {key: 'REQUESTER'}
};

/**
 * Width every value of a group takes in the row that holds it.
 */
const WIDTH_CLASS = {
    full: 'col-12 px-1',
    half: 'col-sm-12 col-md-6 px-1',
    quarter: 'col-sm-12 col-md-6 col-lg-3 px-1'
};

/**
 * Values of the vehicle that takes the poultry out of the plant, in the order
 * the requester fills them. The truck pulls thermo trailers instead of a
 * trailer of its own, so it is read with the dolly and the license of the
 * driver, every one of them completed by the guard at the gate.
 */
const POULTRY_TRUCK_VEHICLE_ROWS = [
    {trans: 'tractor', field: 'tractor', registered: true},
    {trans: 'dolly_no', field: 'dollyNo', registered: true},
    {trans: 'driver_s_name', field: 'driversName', registered: true},
    {trans: 'license_no', field: 'licenseNo', registered: true}
];

/**
 * Packaging of a crates and pallets return, in the order the requester counts
 * them. The quantities the folio expects are the ones the guard completes at
 * the gate, so they are the only count the movement is read with.
 */
/**
 * Field the count of a packaging is stored in, the one its history is read with.
 *
 * The packaging is counted once, so the quantity the requester expected and the
 * one the guard completed at the gate are the same value.
 */
const PACKAGING_QUANTITY_FIELD = 'expectedQuantity';

const PACKAGING_ROWS = [
    {trans: 'large_boxes', field: 'largeBoxes', width: 'quarter', packaging: true},
    {trans: 'small_boxes', field: 'smallBoxes', width: 'quarter', packaging: true},
    {trans: 'lids', field: 'lids', width: 'quarter', packaging: true},
    {trans: 'pallets', field: 'pallets', width: 'quarter', packaging: true}
];

/**
 * Values of a thermo trailer, in the order the requester captures them. The
 * notes are read apart because they take the whole row.
 */
const THERMO_TRAILER_ROWS = [
    {trans: 'destination', field: 'destination'},
    {trans: 'thermo_no', field: 'thermoNo'},
    {trans: 'seal_1_no', field: 'seal1No'},
    {trans: 'seal_2_no', field: 'seal2No'},
    {trans: 'aviso_czm', field: 'avisoCzmNo'},
    {trans: 'dispatch_note_no', field: 'dispatchNoteNo'},
    {trans: 'notes', field: 'notes', width: 'full'}
];

/**
 * Name the history of a thermo trailer stores each field with, for the ones the
 * detail reads with a different name.
 */
const THERMO_TRAILER_AUDIT_FIELDS = {
    avisoCzmNo: 'avisoCzm'
};

/**
 * Body of the list of a form type.
 *
 * - header: icon and title of the card that holds the list.
 * - sections: groups of the gate log, in the order they are registered. A
 *   section either declares the transport of the movement, the values of a
 *   group, or the materials the movement carries.
 * - materials: how the materials are read in every moment of the movement.
 * - materialsCard: card the materials are read in when they are not part of the
 *   gate log, because the movement registers them apart from what the guard
 *   wrote at the gate.
 *
 * A section declares its values with `rows`, reads them from a nested object
 * with `source`, and marks with `registered` the ones the guard at the gate
 * completes, because only those carry the chip of the user that registered them.
 */
const LIST_SECTIONS_DEFAULTS = {
    header: {Icon: IconDocumentVehicleGate, title: 'gate_log'},
    sections: [],
    materials: null,
    materialsCard: null
};

const LIST_SECTIONS_BY_FORM = {
    [ENTRY_AND_EXIT_FORMS.GENERAL_ENTRY_EXIT.key]: {
        header: {Icon: CubeIcon, title: 'materials'},
        sections: [{materials: true}],
        materials: {
            [PHASE.OPEN]: MATERIALS_MODE.EVIDENCE,
            [PHASE.DISCREPANCY]: MATERIALS_MODE.CARD,
            [PHASE.DISPATCHED]: MATERIALS_MODE.CARD
        }
    },
    [ENTRY_AND_EXIT_FORMS.PLANT_MATERIAL_CONTROL.key]: {
        sections: [
            {
                Icon: IconDeliveryTruck,
                title: 'transport',
                rows: [
                    {
                        trans: 'carrier_company',
                        field: 'carrierCompany',
                        width: 'full',
                        registered: true,
                        registeredBy: 'CARRIER'
                    },
                    {
                        trans: 'vehicle_information',
                        field: 'vehicleInformation',
                        width: 'full',
                        registered: true,
                        registeredBy: 'CARRIER'
                    }
                ]
            },
            {
                // The group is the movement the folio takes, so it is read as
                // the entry of the materials when they come into the plant and
                // as their exit when they leave it
                Icon: IconArrowExit,
                title: 'exit',
                titleByDirection: true,
                rows: [
                    {trans: 'date', field: 'date', registered: true, registeredBy: 'MOVEMENT_DATE'},
                    {trans: 'time', field: 'time', registered: true, registeredBy: 'MOVEMENT_DATE'}
                ],
                materials: true
            }
        ],
        // The material of a plant material control is read as a row of the
        // movement it belongs to, with the direction it took at the gate and
        // the user that registered it, whatever the folio is waiting for: what
        // the requester asked for and what the gate wrote are the same list
        materials: {
            [PHASE.OPEN]: MATERIALS_MODE.DISPATCHED,
            [PHASE.DISCREPANCY]: MATERIALS_MODE.DISPATCHED,
            [PHASE.DISPATCHED]: MATERIALS_MODE.DISPATCHED
        }
    },
    [ENTRY_AND_EXIT_FORMS.CRATES_AND_PALLETS_RETURN.key]: {
        sections: [
            {transport: true},
            {
                Icon: IconCube3d,
                title: 'expected_quantities',
                rows: PACKAGING_ROWS
            }
        ]
    },
    [ENTRY_AND_EXIT_FORMS.POULTRY_TRUCK_EXIT.key]: {
        sections: [
            {transport: true, rows: POULTRY_TRUCK_VEHICLE_ROWS},
            {
                Icon: IconTrailer,
                title: 'thermo_trailer_1',
                source: 'thermoTrailer1',
                rows: THERMO_TRAILER_ROWS
            },
            {
                // The truck pulls a second thermo trailer only when it needs
                // one, so the group says it is optional the same way the form
                // that captures it does
                Icon: IconTrailer,
                title: 'thermo_trailer_2_optional',
                source: 'thermoTrailer2',
                rows: THERMO_TRAILER_ROWS
            }
        ]
    },
    [ENTRY_AND_EXIT_FORMS.LIVE_POULTRY_TRUCK.key]: {
        sections: [
            {
                Icon: IconDeliveryTruck,
                title: 'transport',
                rows: [
                    // The transport is optional for the requester, so a value
                    // without a history travelled with the request
                    {trans: 'tractor', field: 'tractor', registered: true, registeredBy: 'REQUESTER'},
                    {trans: 'dolly_no', field: 'dollyNo', registered: true, registeredBy: 'REQUESTER'},
                    {trans: 'driver_s_name', field: 'driversName', registered: true, registeredBy: 'REQUESTER'},
                    {trans: 'license_no', field: 'licenseNo', registered: true, registeredBy: 'REQUESTER'}
                ]
            },
            {
                // The truck comes into the plant or leaves it, so the group is
                // named by the direction of the folio; one registered without
                // a direction is read as an entry, the same way the guard reads it
                Icon: IconArrowEntry,
                title: 'entry',
                titleByDirection: true,
                rows: [
                    {trans: 'date', field: 'date', registered: true, registeredBy: 'MOVEMENT_DATE'},
                    {trans: 'time', field: 'time', registered: true, registeredBy: 'MOVEMENT_DATE'},
                    {trans: 'platform_1_no', field: 'platform1No', folio: true},
                    {trans: 'platform_2_no', field: 'platform2No', folio: true},
                    // The observations are written on the folio as its extra
                    // details, so they are read with the movement they describe
                    {trans: 'observations', field: 'extraDetails', folio: true, width: 'full'}
                ]
            }
        ]
    }
};

/**
 * Returns the value of a row of the gate log, or the notice that it is still
 * waiting for whoever registers it.
 *
 * A quantity counted at the gate can be zero, which is a count of its own and
 * not a value left blank, so only what was never captured is read as pending.
 *
 * A movement the gate already dispatched is not waited for any more, so what it
 * was closed without is read as a value the folio does not carry instead of as
 * one that is still expected.
 *
 * @param {*} value - Value registered for the row.
 * @param {string} emptyText - Text a value nobody wrote is read with.
 *
 * @returns {*} Value of the row, or the notice of the value it was left without.
 */
const getRowValue = (value, emptyText) => (
    hasGateLogValue(value) ? value : emptyText
);

/**
 * Returns the list declared by the given form type.
 *
 * The forms registered before the type was captured are read as a general entry
 * and exit, which is the movement every plant registers.
 *
 * @param {string} formKey - Key of the form type.
 *
 * @returns {Object} List the detail of that form displays.
 */
const getListSections = (formKey) => ({
    ...LIST_SECTIONS_DEFAULTS,
    ...(LIST_SECTIONS_BY_FORM[formKey] ||
        LIST_SECTIONS_BY_FORM[ENTRY_AND_EXIT_FORMS.GENERAL_ENTRY_EXIT.key])
});

/**
 * Returns the moment the movement is read in.
 *
 * @param {number} dispatchStatusId - Dispatch status of the folio.
 *
 * @returns {string} Phase of the movement.
 */
const getPhase = (dispatchStatusId) => {
    if (dispatchStatusId === STATUS_MODEL.DISCREPANCY)
        return PHASE.DISCREPANCY;

    // The departure takes the material out of the plant, so a folio that
    // already left, or that only waits for the vehicle to leave, is read the
    // same way the dispatched one is: every value the gate registered with the
    // user that wrote it
    if ([
        STATUS_MODEL.DISPATCHED,
        STATUS_MODEL.PENDING_DEPARTURE,
        STATUS_MODEL.DEPARTURE_RECORDED
    ].includes(dispatchStatusId))
        return PHASE.DISPATCHED;

    return PHASE.OPEN;
};

/**
 * Materials the movement carries, read as the moment of the folio displays them.
 *
 * The dispatch is what closes the folio, so the materials of a dispatched
 * movement are read with the guard that registered it at the gate and the date
 * the movement was written.
 *
 * @returns {JSX.Element} List of the materials of the folio.
 */
const FolioMaterials = ({t, data, list, phase, registeredAt}) => {
    // Every material says who wrote it in the history of the folio, so this is
    // only what the ones registered before that history was kept are read with:
    // the materials of a movement the gate closed were written there, and the
    // ones of a folio that never reached it travelled with the request. The
    // transport is not read here, because who completed the vehicle says
    // nothing about who wrote the materials.
    const source = phase === PHASE.DISPATCHED
        ? {name: data?.dispatchUser, date: registeredAt}
        : {name: data?.requestedBy, date: data?.createdAt};

    return (
        <MaterialsSection
            t={t}
            item={data}
            items={data?.items || []}
            mode={list.materials[phase]}
            // The evidence of a material is a draft of the gate until the
            // dispatch is registered, so a folio reported after it was
            // dispatched still reads it and the phase alone does not say so,
            // because the report replaces it
            dispatched={wasMaterialControlDispatched(data)}
            registeredBy={source.name}
            registeredAt={source.date}
        />
    );
};

/**
 * Body of the card: the groups declared by the form, with the materials the
 * movement carries in the group that registers them.
 */
const ListSectionsBody = ({t, data, list, phase, registeredAt}) => {
    const dispatched = phase === PHASE.DISPATCHED;
    // Every value the gate never wrote is read the same way across the log, so
    // a folio that is still open says that it waits for it and one that already
    // dispatched its materials reads it as a value it does not carry
    const emptyText = getGateLogEmptyText(data, t);
    const gateSource = getGateRegisteredSource(data, registeredAt);
    // The date of the movement is written on the folio, so the change the gate
    // wrote over it is the one that says the guard registered it
    const movementDateAudit = getFieldAudit(
        data,
        MATERIAL_CONTROL_AUDIT_ENTITY.MATERIAL_CONTROL,
        'expectedMovementDate',
        data?.id
    );
    // The value the carrier handed over reads the driver the transport travels
    // with, and the transport that carries no driver of its own travels with
    // the folio the way it was asked for, so it is named by the requester that
    // registered it; the change the gate wrote over it is the one that names
    // the guard, because the history of the field is read before every source.
    const registeredSource = {
        CARRIER: {
            name: data?.transport?.driverName || data?.requestedBy || null,
            date: data?.createdAt ?? registeredAt
        },
        GUARD: gateSource,
        // The date of the movement is written on the folio itself, so its
        // history is the one that names who wrote it: a date the gate completed
        // carries the change that registered it, and the one that never changed
        // is the one the requester registered the folio with
        //
        // A folio that carries no date of its own reads the moment the gate
        // stamped when it dispatched it, so that date is named by the gate
        MOVEMENT_DATE: movementDateAudit
            ? {name: movementDateAudit.changedBy, date: movementDateAudit.changedAt}
            : (data?.expectedMovementDate != null
                ? {name: data?.requestedBy || null, date: data?.createdAt}
                : gateSource),
        // A value that was never changed is the one the folio was registered
        // with, so it is named by the requester that asked for the movement
        REQUESTER: {name: data?.requestedBy || null, date: data?.createdAt}
    };
    // A value completed after the folio was registered carries its own history,
    // so the chip reads the user that wrote that field and the moment it was
    // written; the values the service does not keep a history of fall back to
    // the user that closed the folio.
    //
    // A field the gate has not written yet has nobody to name, so the value
    // that is still blank is read without a chip and only the ones that already
    // carry information name the user that registered them.
    const registeredChip = (registeredBy = REGISTERED_BY.GUARD, field = null, value = undefined, fieldAudit = undefined) => {
        // The history names every record of a block apart, so the change is
        // read from the transport the folio travels with, unless the value is
        // kept in a record of its own and brings the change of that record
        // The date and the time of the movement are written on the folio
        // itself, so they read the history of the date the folio carries.
        const audit = fieldAudit !== undefined
            ? fieldAudit
            : registeredBy === REGISTERED_BY.MOVEMENT_DATE
                ? movementDateAudit
                : (field
                    ? getFieldAudit(
                        data,
                        MATERIAL_CONTROL_AUDIT_ENTITY.TRANSPORT,
                        field,
                        data?.transport?.id
                    )
                    : null);
        // The value of a field says by itself whether it was registered, and
        // the block that reads no field of its own is the one the dispatch
        // closes, so only that one is named by the moment of the folio
        const isRegistered = field === null
            ? (dispatched || audit !== null)
            : hasGateLogValue(value);

        if (!isRegistered)
            return null;

        const source = registeredSource[registeredBy.key];
        const name = audit?.changedBy || source.name;

        // The history is only written at the gate, so a value that carries one
        // is always named with the icon of the guard, even when the same user
        // registered the folio
        return <RegisteredByChip
            Icon={audit ? IconGuardTitle : getRegisteredByIcon(data, name)}
            name={name}
            date={audit?.changedAt ?? source.date}
        />;
    };
    // A value of the vehicle is written by the requester when the folio travels
    // with it and by the guard when it was left blank, so the history of the
    // field is the one that names the guard and the request names the rest
    const requesterChip = (field, value) => registeredChip(REGISTERED_BY.REQUESTER, field, value);

    // Every packaging is counted in a record of its own, so its count is named
    // by the history of that record: the quantity the gate completed carries
    // the change that wrote it, and the one that never changed is the one the
    // requester registered the folio with
    const packagingChip = (field, value) => {
        const record = getPackagingQuantityRecord(data, field);
        const audit = record
            ? getFieldAudit(
                data,
                MATERIAL_CONTROL_AUDIT_ENTITY.PACKAGING_QUANTITY,
                PACKAGING_QUANTITY_FIELD,
                record.id
            )
            : null;

        return registeredChip(REGISTERED_BY.REQUESTER, field, value, audit);
    };

    // Every thermo trailer is a record of its own, so its values are named by
    // the history of that record: the value the gate completed carries the
    // change that wrote it, and the one that never changed travelled with the
    // request
    const trailerChip = (trailer, field, value) => {
        const audit = trailer?.id
            ? getFieldAudit(
                data,
                MATERIAL_CONTROL_AUDIT_ENTITY.TRAILER,
                THERMO_TRAILER_AUDIT_FIELDS[field] || field,
                trailer.id
            )
            : null;

        return registeredChip(REGISTERED_BY.REQUESTER, field, value, audit);
    };

    // A value written on the folio itself is named by the history of the folio:
    // the one the gate completed carries the change that wrote it, and the one
    // that never changed travelled with the request
    const folioChip = (field, value) => registeredChip(
        REGISTERED_BY.REQUESTER,
        field,
        value,
        getFieldAudit(data, MATERIAL_CONTROL_AUDIT_ENTITY.MATERIAL_CONTROL, field, data?.id)
    );

    // The materials read in a card of their own are not part of the gate log,
    // so the groups of the log do not display them
    const materials = list.materials && !list.materialsCard &&
        <FolioMaterials
            t={t}
            data={data}
            list={list}
            phase={phase}
            registeredAt={registeredAt}
        />;

    return (
        <>
            {list.sections.map((section, index) => {
                if (section.transport)
                    return (
                        <div className={"mb-3"} key={index}>
                            <TransportSection
                                t={t}
                                transport={data}
                                rows={section.rows}
                                // Every value of the vehicle is optional for the
                                // requester, so each one is named by its own
                                // history: the value the gate completed carries
                                // the change that wrote it and the one that
                                // never changed travelled with the request
                                carrierAction={requesterChip}
                                vehicleAction={requesterChip}
                                emptyText={emptyText}
                            />
                        </div>
                    );

                if (!section.Icon)
                    return (
                        <div className={"row my-3"} key={index}>
                            <div className={"col-12"}>
                                {materials}
                            </div>
                        </div>
                    );

                const values = section.source ? data?.[section.source] : data;
                // A group that reads the movement of the folio is named by the
                // direction it takes, so the same group is the entry of the
                // materials or their exit
                const sectionDirection = section.titleByDirection
                    ? getMovementDirectionOptionByValue(data?.movementDirection)
                    : null;
                const isEntrySection = sectionDirection?.key === MOVEMENT_DIRECTION.ENTRY;

                return (
                    <div className={"mb-3"} key={index}>
                        <GateLogGroup
                            Icon={sectionDirection
                                ? (isEntrySection ? IconArrowEntry : IconArrowExit)
                                : section.Icon}
                            title={t(sectionDirection?.trans || section.title)}
                        >
                            <div className={"row"}>
                                {section.rows?.map(row => {
                                    const value = values?.[row.field];
                                    // A quantity is counted in a record of its
                                    // own, so it is named by the history of
                                    // that record and not by the one of the
                                    // block the rest of the group is read from
                                    const action = row.packaging
                                        ? packagingChip(row.field, value)
                                        : row.folio
                                            ? folioChip(row.field, value)
                                            : section.source
                                                ? trailerChip(values, row.field, value)
                                                : row.registered
                                                    ? registeredChip(
                                                        REGISTERED_BY[row.registeredBy],
                                                        row.field,
                                                        value
                                                    )
                                                    : null;

                                    return (
                                        // The box fills the row it is read in,
                                        // so the values that name who wrote
                                        // them and the ones that are still
                                        // pending are the same height
                                        <div key={row.field} className={`${WIDTH_CLASS[row.width || 'half']} mb-3`}>
                                            <GateLogValueBox
                                                label={t(row.trans)}
                                                value={getRowValue(value, emptyText)}
                                                action={action}
                                                className={"h-100"}
                                            />
                                        </div>
                                    );
                                })}
                            </div>

                            {section.materials && materials}
                        </GateLogGroup>
                    </div>
                );
            })}
        </>
    );
};

/**
 * List of what the entry and exit form registers.
 *
 * Every form type reads the same card, so the list is built once and the type
 * of the form only declares the groups it captures. The status of the folio
 * decides how they are read: the movement that is still open shows what was
 * asked for, the one with discrepancies opens with the review that has to be
 * attended before the dispatch, and the dispatched one shows who registered
 * every value at the gate.
 *
 * @returns {JSX.Element} Card with the list of the folio.
 */
const EntryAndExitFormListSection = ({data, form, contentTableRef}) => {
    const {t} = useTranslation();
    const list = getListSections(form?.key);
    const phase = getPhase(data?.dispatchStatusId);

    // The gate writes the log when it closes the folio, so every value it
    // registered is read with the date of the movement that closed it
    const registeredAt = data?.dispatchDate ?? null;

    // The materials the movement registers apart from the gate log are read
    // below it, in the card the form declares for them
    const materialsCard = list.materialsCard &&
        <GateLogCard
            t={t}
            Icon={list.materialsCard.Icon}
            title={list.materialsCard.title}
        >
            <FolioMaterials
                t={t}
                data={data}
                list={list}
                phase={phase}
                registeredAt={registeredAt}
            />
        </GateLogCard>;

    // The report of the discrepancies is what has to be attended, and the values
    // of the folio are read on their own, so the review opens the detail and the
    // list of the folio is displayed below it and not inside the report.
    if (phase === PHASE.DISCREPANCY)
        return (
            <div ref={contentTableRef} className={"overflow-y-auto"}>
                <div className={"border-radius-8px border-full-shades-300 pb-3 mb-3"}>
                    <div className='px-3 d-flex justify-content-start align-items-center h-72px border-radius-top-8px background-shades-50 border-bottom-shades-300'>
                        <div className={"mr-2 d-flex align-items-center"}>
                            <IconDocumentWarning/>
                        </div>
                        <p className={"fs-16px ibl-font-w-600"}>{t("discrepancy_details")}</p>
                    </div>

                    <DiscrepancyBanner
                        t={t}
                        reviewedBy={data?.dispatchUser}
                        reviewedAt={registeredAt}
                        reason={data?.dispatchReason}
                        files={data?.discrepancyFiles}
                    />
                </div>

                <div className={materialsCard ? "mb-3" : ""}>
                    <GateLogCard t={t} Icon={list.header.Icon} title={list.header.title}>
                        <ListSectionsBody
                            t={t}
                            data={data}
                            list={list}
                            phase={phase}
                            registeredAt={registeredAt}
                        />
                    </GateLogCard>
                </div>

                {materialsCard}
            </div>
        );

    // The materials registered apart from the gate log are read in a card of
    // their own, so both cards scroll together instead of one inside the other
    if (materialsCard)
        return (
            <div ref={contentTableRef} className={"overflow-y-auto"}>
                <div className={"mb-3"}>
                    <GateLogCard t={t} Icon={list.header.Icon} title={list.header.title}>
                        <ListSectionsBody
                            t={t}
                            data={data}
                            list={list}
                            phase={phase}
                            registeredAt={registeredAt}
                        />
                    </GateLogCard>
                </div>

                {materialsCard}
            </div>
        );

    return (
        <GateLogCard
            t={t}
            Icon={list.header.Icon}
            title={list.header.title}
            contentRef={contentTableRef}
        >
            <ListSectionsBody
                t={t}
                data={data}
                list={list}
                phase={phase}
                registeredAt={registeredAt}
            />
        </GateLogCard>
    );
};

export default EntryAndExitFormListSection;
